/* eslint-disable react/no-array-index-key */
import React from 'react';

import type { PriceType } from '../../models/priceData/priceData';
import type { ProductVariantProps } from '../../models/props/itemTeaserProps';
import type { A11yTextType } from '../A11yTextWrapper/A11yTextWrapper';
import type { ProductSearchListingItemData } from './ProductSearchListingItem.type';

import { getPrePurchaseProductPriceGroups } from '../../../cart/components/molecules/PrepurchaseProductPriceLabels/PrepurchaseProductPriceLabels';
import { getPrePurchaseSpecFilter } from '../../../cart/components/molecules/PrepurchaseProductPriceLabels/PrePurchaseSpecFilter';
import renderProductPriceGroups, {
    defineProductPriceGroup,
} from '../../../ecommerce/utils/renderProductPriceGroups';

type Params = {
    product: ProductSearchListingItemData;
    variant: ProductVariantProps | undefined;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
};

export const getProductSearchListingItemPriceA11yText = (params: Params) => {
    const labels = getPriceGroups(params);
    return {
        primaryText: labels.primary.a11y,
        secondaryText: labels.secondary.a11y,
    };
};

const ProductSearchListingItemPriceLabels = React.memo(
    (props: Params & { type: 'primary' | 'secondary' }) => {
        const labels = getPriceGroups(props);
        return labels[props.type].render;
    },
);

export default ProductSearchListingItemPriceLabels;

const getPriceGroups = (params: Params) => {
    const base = getPrePurchaseProductPriceGroups(params);
    const shouldRender = getPrePurchaseSpecFilter(params.product);

    const primarySpecSet = (['primaryPrice', 'primaryComparativePrice'] as const).filter((spec) =>
        shouldRender.primary({
            spec,
            product: params.product,
            priceType: params.priceType,
            a11yTextType: params.a11yTextType,
            isB2B: params.isB2B,
            isUserMember: params.isUserMember,
        }),
    );
    const secondarySpecSet = (
        [
            'primaryPrice',
            'maxPromoUsage',
            'recycleFee',
            'promoComparativePrice',
            'originalPrice',
            'comparativePrice',
            'historicalPrice',
            'excludingTax',
        ] as const
    ).filter((spec) =>
        shouldRender.secondary({
            spec,
            product: params.product,
            priceType: params.priceType,
            a11yTextType: params.a11yTextType,
            isB2B: params.isB2B,
            isUserMember: params.isUserMember,
        }),
    );

    return renderProductPriceGroups(
        () => `${params.product.identifier}_${params.variant?.id}`,
        [
            defineProductPriceGroup({
                type: 'primary',
                specSet: primarySpecSet,
                createLabel: base.definitions.primary.createLabel,
            }),
            defineProductPriceGroup({
                type: 'secondary',
                specSet: secondarySpecSet,
                createLabel: base.definitions.secondary.createLabel,
            }),
        ],
    );
};
